import { ref } from 'vue'
import { readPref, writePref } from './viewPrefs.js'

/**
 * Larger letters by spreading two fingers (D-233), on this device: the gesture a phone uses to
 * magnify part of the screen sizes the application's text instead — read on the go, in a car.
 * Text grows furthest; buttons, tabs, fields and the menus less; the layout's spacing least,
 * so the basic buttons still fit the screen. Maps keep the gesture for themselves.
 */
export const ZOOM = { min: 0.85, max: 1.8, controls: 1.3, layoutMin: 0.85, layoutMax: 1.15 }

export const textZoom = ref(1)

/** The three factors of one zoom: the layout (rem), the text and the controls on top of it. */
export function zoomFactors(zoom) {
  const z = Math.min(ZOOM.max, Math.max(ZOOM.min, Number(zoom) || 1))
  const layout = Math.min(ZOOM.layoutMax, Math.max(ZOOM.layoutMin, z))
  return { zoom: z, layout, text: z / layout, controls: Math.min(z, ZOOM.controls) / layout }
}

/** A zoom close to the usual size is the usual size; others in steps of 5 %. */
export function snapZoom(zoom) {
  const z = Math.round(zoom * 20) / 20
  return Math.abs(z - 1) < 0.04 ? 1 : Math.min(ZOOM.max, Math.max(ZOOM.min, z))
}

export function applyTextZoom(zoom) {
  const f = zoomFactors(zoom)
  textZoom.value = f.zoom
  const root = document.documentElement.style
  root.setProperty('--zoom-layout', String(f.layout))
  root.setProperty('--zoom-text', String(f.text))
  root.setProperty('--zoom-controls', String(f.controls))
}

export function setTextZoom(zoom) {
  applyTextZoom(snapZoom(zoom))
  writePref(null, 'textZoom', textZoom.value)
}

export function restoreTextZoom() {
  applyTextZoom(readPref(null, 'textZoom', 1))
}

/** Pinching on a touch screen sizes the text; `onChange(zoom, done)` shows it as it goes. */
export function installPinchZoom(onChange = () => {}) {
  if (typeof window === 'undefined' || !window.matchMedia?.('(pointer: coarse)').matches) return () => {}
  let start = null
  let frame = 0
  const distance = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY)
  const ownGesture = (target) => !!target?.closest?.('.leaflet-container, [data-own-pinch]')
  const begin = (e) => {
    if (e.touches.length !== 2 || ownGesture(e.target)) return
    start = { distance: distance(e.touches) || 1, zoom: textZoom.value }
  }
  const move = (e) => {
    if (!start || e.touches.length !== 2) return
    e.preventDefault()
    const next = snapZoom(start.zoom * (distance(e.touches) / start.distance))
    if (next === textZoom.value) return
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      applyTextZoom(next)
      onChange(next, false)
    })
  }
  const end = (e) => {
    if (!start || e.touches.length >= 2) return
    start = null
    setTextZoom(textZoom.value)
    onChange(textZoom.value, true)
  }
  // Safari's own pinch-to-magnify, which it does not let the page's viewport switch off.
  const stopGesture = (e) => e.preventDefault()
  document.addEventListener('touchstart', begin, { passive: true })
  document.addEventListener('touchmove', move, { passive: false })
  document.addEventListener('touchend', end, { passive: true })
  document.addEventListener('touchcancel', end, { passive: true })
  document.addEventListener('gesturestart', stopGesture)
  document.addEventListener('gesturechange', stopGesture)
  return () => {
    document.removeEventListener('touchstart', begin)
    document.removeEventListener('touchmove', move)
    document.removeEventListener('touchend', end)
    document.removeEventListener('touchcancel', end)
    document.removeEventListener('gesturestart', stopGesture)
    document.removeEventListener('gesturechange', stopGesture)
  }
}
